/**
 * Parse the buffered output from an impact dump command ("d").
 * Lines are everything between <<<IMPACT_DUMP_START>>> and <<<IMPACT_DUMP_END>>>.
 *
 * @param {string[]} lines
 * @returns {{ mac: string|null, fileCount: number, files: object[], totalImpacts: number, error: string|null }}
 */
export function parseImpactDump(lines) {
  let mac = null;
  let fileCount = 0;
  const files = [];
  let error = null;

  // State machine: IDLE → IN_FILE → IN_DATA
  let state = "IDLE";
  let currentFile = null;

  for (const raw of lines) {
    const line = raw.trim();

    // Capture errors at any point
    if (line.startsWith("ERROR")) {
      error = line;
      continue;
    }

    if (state === "IDLE") {
      if (line.startsWith("MAC:")) {
        mac = line.substring("MAC:".length).trim();
      } else if (line.startsWith("FILE_COUNT:")) {
        fileCount = parseInt(line.split(":")[1], 10) || 0;
        if (fileCount === 0) {
          return { mac, fileCount: 0, files: [], totalImpacts: 0, error };
        }
      } else if (line.startsWith("<<<FILE_START:")) {
        state = "IN_FILE";
        currentFile = { filename: null, size: null, impacts: [] };
      }
    } else if (state === "IN_FILE") {
      if (line.startsWith("FILENAME:")) {
        currentFile.filename = line.substring("FILENAME:".length).trim();
      } else if (line.startsWith("SIZE:")) {
        currentFile.size = parseInt(line.substring("SIZE:".length).trim(), 10);
      } else if (line === "<<<DATA_START>>>") {
        state = "IN_DATA";
      } else if (line.startsWith("<<<FILE_END:")) {
        files.push(currentFile);
        currentFile = null;
        state = "IDLE";
      }
    } else if (state === "IN_DATA") {
      if (line === "<<<DATA_END>>>") {
        state = "IN_FILE";
      } else if (line.startsWith("HEADER:")) {
        // Skip header line
        continue;
      } else if (line.length > 0) {
        // CSV: boot,impact_id,timestamp_ms,linear_accel,rotational_accel,x_centi_g,y_centi_g,z_centi_g
        // Column encodings (firmware-side, see event_storage.cpp):
        //   boot             int    — boot counter, raw
        //   impact_id        uint16 — raw
        //   timestamp_ms     uint64 — Unix UTC ms if time-synced, else ms-since-boot
        //   linear_accel     uint16 — 0–65535 maps to 0–200 g   → g = raw / 65535 * 200
        //   rotational_accel uint8  — 0–255 maps to 0–10000      → rad/s² = raw / 255 * 10000
        //   x/y/z_centi_g    int16  — signed 0.01 g units        → g = raw / 100
        const parts = line.split(",");
        if (parts.length >= 5) {
          const linearRaw = parseInt(parts[3], 10);
          const rotationalRaw = parseInt(parts[4], 10);
          const impact = {
            boot: parseInt(parts[0], 10),
            impactId: parseInt(parts[1], 10),
            timestampMs: parseInt(parts[2], 10),
            linearG: parseFloat(((linearRaw / 65535) * 200).toFixed(1)),
            rotationalRadS2: parseFloat(
              ((rotationalRaw / 255) * 10000).toFixed(1)
            ),
          };
          if (parts.length >= 8) {
            // Signed int16 in centi-g (0.01 g). parseInt handles the sign;
            // dividing by 100 recovers g (range ±327.67, 0.01 g resolution).
            impact.axG = parseInt(parts[5], 10) / 100;
            impact.ayG = parseInt(parts[6], 10) / 100;
            impact.azG = parseInt(parts[7], 10) / 100;
          }
          if (parts.length >= 9) {
            impact.source = parts[8].trim();
          }
          currentFile.impacts.push(impact);
        }
      }
    }
  }

  const totalImpacts = files.reduce((sum, f) => sum + f.impacts.length, 0);
  return { mac, fileCount, files, totalImpacts, error };
}

/**
 * Parse the buffered output from a battery history command ("b").
 * Lines are everything between the start/end markers.
 *
 * @param {string[]} lines
 * @returns {{ entries: object[], isEmpty: boolean }}
 */
export function parseBatteryHistory(lines) {
  // Check for empty response
  for (const line of lines) {
    if (line.trim().includes("No battery history available")) {
      return { entries: [], isEmpty: true };
    }
  }

  const entries = [];

  for (const raw of lines) {
    const line = raw.trim();
    // Skip non-data lines
    if (!line.includes("|")) continue;

    const parts = line.split("|").map((p) => p.trim());
    if (parts.length < 4) continue;

    // Skip header row
    if (parts[0] === "ID" || parts[0].toUpperCase() === "ID") continue;

    const id = parts[0];
    const boot = parts[1] === "N/A" ? null : parseInt(parts[1], 10);
    const timeSeconds = parts[2] === "N/A" ? null : parseInt(parts[2], 10);
    const batteryPercent =
      parts[3] === "N/A" ? null : parseFloat(parts[3].replace("%", ""));

    entries.push({ id, boot, timeSeconds, batteryPercent });
  }

  return { entries, isEmpty: entries.length === 0 };
}

/**
 * Parse the buffered output from a clear files command ("c").
 * Lines are everything between the start/end markers.
 *
 * @param {string[]} lines
 * @returns {{ filesRemoved: string[], filesPreserved: string[], success: boolean }}
 */
export function parseClearFiles(lines) {
  const filesRemoved = [];
  const filesPreserved = [];

  for (const raw of lines) {
    const line = raw.trim();

    if (line.includes("Removing:")) {
      const path = line
        .substring(line.indexOf("Removing:") + "Removing:".length)
        .trim();
      if (path) filesRemoved.push(path);
    } else if (line.includes("Preserving:")) {
      const path = line
        .substring(line.indexOf("Preserving:") + "Preserving:".length)
        .trim();
      if (path) filesPreserved.push(path);
    }
  }

  // End marker being reached confirms completion
  return { filesRemoved, filesPreserved, success: true };
}
